import { Button, Grid, TextField, Autocomplete } from '@mui/material';
import { Box } from '@mui/system';
import axios from 'axios';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TOKEN_KEY, url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import { IExecutiveGroup } from './types';

type EditExecutiveGroupProps = {
  handleClose: () => void;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
  group: IExecutiveGroup | null;
};

type YearOption = {
  label: string;
  value: string;
};

type FormValues = {
  durationYears: YearOption[];
};

// helper: parse "2024-2025" or "2024" -> [2024, 2025] or [2024]
const parseDurationToYears = (duration?: string): YearOption[] => {
  if (!duration) return [];

  const trimmed = duration.trim();
  if (!trimmed) return [];

  const parts = trimmed.split('-').map((p) => p.trim());

  let years: number[] = [];
  if (parts.length === 1) {
    const y = Number(parts[0]);
    if (!Number.isNaN(y)) years = [y];
  } else {
    const start = Number(parts[0]);
    const end = Number(parts[1]);
    if (!Number.isNaN(start) && !Number.isNaN(end) && end >= start) {
      years = Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }
  }

  return years.map((y) => ({ label: `${y}`, value: `${y}` }));
};

function EditExecutiveGroup({
  handleClose,
  setRefreshTrigger,
  group,
}: EditExecutiveGroupProps) {
  const defaultDurationYears = parseDurationToYears(group?.duration);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      durationYears: defaultDurationYears,
    },
  });

  const [loading, setLoading] = useState<boolean>(false);
  const Toast = Toaster();
  const token = localStorage.getItem(TOKEN_KEY);

  const onSubmit = async (values: FormValues) => {
    if (!group?.id) return;

    if (!values.durationYears || values.durationYears.length === 0) {
      Toast.fire({
        icon: 'warning',
        title: 'Please select at least one year for duration.',
      });
      return;
    }

    const years = values.durationYears
      .map((y) => Number(y.value))
      .filter((y) => !Number.isNaN(y));

    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);

    const newDuration =
      minYear === maxYear ? `${minYear}` : `${minYear}-${maxYear}`;

    const oldDuration = (group.duration || '').trim();

    // ✅ if duration is same as previous, do not call backend
    if (newDuration === oldDuration) {
      Toast.fire({
        icon: 'info',
        title: 'No changes detected to update.',
      });
      return;
    }

    try {
      if (!token) {
        Toast.fire({
          icon: 'error',
          title: 'Authentication token is missing. Please log in again.',
        });
        return;
      }

      setLoading(true);

      const payload = {
        duration: newDuration,
      };

      const { data } = await axios.patch(
        `${url}/api/v1/admin/executive/groups/${group.id}`, // adjust if your endpoint differs
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (data.success) {
        Toast.fire({
          icon: 'success',
          title: `${data.message}`,
        });
        setRefreshTrigger((prev: number) => prev + 1);
        reset({ durationYears: parseDurationToYears(newDuration) });
        handleClose();
      } else {
        Toast.fire({
          icon: 'error',
          title: `${data.message}`,
        });
      }
    } catch (err: any) {
      Toast.fire({
        icon: 'error',
        title: `${err?.response?.data?.message || 'Something went wrong'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  // year options 2000–2099
  const yearOptions: YearOption[] = Array.from({ length: 100 }, (_, i) => {
    const year = 2000 + i;
    return { label: `${year}`, value: `${year}` };
  });

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <Grid container px={1} spacing={1}>
        <Grid item xs={12}>
          <Controller
            control={control}
            name='durationYears'
            render={({ field }) => (
              <Autocomplete
                multiple
                options={yearOptions}
                getOptionLabel={(option) => option.label}
                value={field.value || []}
                onChange={(_, newValue) => {
                  const uniqueValues = Array.from(
                    new Set(newValue.map((item) => item.value))
                  ).map((value) =>
                    newValue.find((item) => item.value === value)
                  ) as YearOption[];

                  field.onChange(uniqueValues);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                    label='Select years for duration'
                    placeholder='Select one or more years'
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disableElevation
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default EditExecutiveGroup;
